import * as Sentry from '@sentry/react';

/**
 * PII Scrubbing for GDPR Compliance
 *
 * This module scrubs 7 categories of sensitive data before sending to Sentry:
 * 1. License numbers (7-10 digits)
 * 2. Phone numbers (German format)
 * 3. IBANs (European format)
 * 4. Mollie tokens (access_*, refresh_*)
 * 5. Email addresses
 * 6. Precise GPS coordinates (rounded to ~1km)
 * 7. Patient counts (Notfallscheinbeteiligung PHI)
 */

// Regex patterns for PII detection
const PII_PATTERNS = {
  licenseNumber: /\b\d{7,10}\b/g, // German medical license: 7-10 digits
  phoneNumber: /\+49\s?\d{2,4}\s?\d{6,10}/g, // +49 30 12345678 or +4930 12345678
  iban: /[A-Z]{2}\d{2}[A-Z0-9]{10,30}/gi, // DE89370400440532013000
  mollieToken: /(?:access|refresh)_[a-zA-Z0-9]{32,}/g, // access_abc123...
  email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
} as const;

/**
 * Main beforeSend hook for Sentry
 * Scrubs PII from all event contexts before transmission
 */
export const scrubbedBeforeSend = (event: Sentry.Event, _hint: Sentry.EventHint): Sentry.Event | null => {
  try {
    // 1. Scrub user context (keep only user ID for debugging)
    if (event.user) {
      event.user = {
        ...(event.user.id ? { id: event.user.id } : {}),
        // Remove: email, username, ip_address
      };
    }

    // 2. Scrub all text-based fields
    event = scrubTextFields(event);

    // 3. Scrub specific sensitive contexts
    event = scrubSensitiveContexts(event);

    // 4. Scrub breadcrumbs (user actions)
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
    }

    // 5. Scrub request data (POST body, query params)
    if (event.request) {
      event.request = scrubRequest(event.request) ?? event.request;
    }

    return event;
  } catch (error) {
    // If scrubbing fails, log error but don't send event
    console.error('[Sentry] PII scrubbing failed:', error);
    return null; // Prevent sending unscrubbed data
  }
};

/**
 * Scrub all string fields in the event using regex patterns
 */
const scrubTextFields = (event: Sentry.Event): Sentry.Event => {
  // Scrub extra context
  if (event.extra) {
    event.extra = scrubObject(event.extra);
  }

  // Scrub tags
  if (event.tags) {
    event.tags = scrubObject(event.tags);
  }

  // Scrub contexts
  if (event.contexts) {
    event.contexts = scrubObject(event.contexts);
  }

  // Scrub message
  if (event.message) {
    event.message = scrubString(event.message);
  }

  return event;
};

/**
 * Scrub sensitive context fields specific to our app
 */
const scrubSensitiveContexts = (event: Sentry.Event): Sentry.Event => {
  if (!event.contexts) return event;

  // Scrub duty context (may contain patient count)
  if (event.contexts['duty']) {
    const duty = event.contexts['duty'] as Record<string, any>;
    delete duty['patientCount'];
    delete duty['notfallscheinbeteiligungAmount'];
  }

  // Scrub user profile context
  if (event.contexts['userProfile']) {
    const userProfile = event.contexts['userProfile'] as Record<string, any>;
    delete userProfile['licenseNumber'];
    delete userProfile['phone'];
    delete userProfile['altPhone'];
    delete userProfile['landline'];
    delete userProfile['kvEmail'];
    delete userProfile['mollieAccessToken'];
    delete userProfile['mollieRefreshToken'];

    // Round GPS coordinates to ~1km accuracy (2 decimals)
    if (userProfile['location'] && Array.isArray(userProfile['location']['coordinates'])) {
      const [lat, lng] = userProfile['location']['coordinates'];
      userProfile['location']['coordinates'] = [Math.round(lat * 100) / 100, Math.round(lng * 100) / 100];
    }
  }

  // Scrub billing info context
  if (event.contexts['billingInfo']) {
    const billingInfo = event.contexts['billingInfo'] as Record<string, any>;
    delete billingInfo['iban'];
    delete billingInfo['bic'];
    delete billingInfo['taxNumber'];
    delete billingInfo['accountHolder'];
    delete billingInfo['address'];
  }

  return event;
};

/**
 * Recursively scrub an object (handles nested objects and arrays)
 */
const scrubObject = (obj: any, parentKey?: string): any => {
  // Handle arrays: scrub each element
  if (Array.isArray(obj)) {
    return obj.map((item) => {
      if (typeof item === 'string') {
        return scrubString(item);
      }
      return scrubObject(item);
    });
  }

  // Handle strings
  if (typeof obj === 'string') {
    // If parent key is sensitive, still scrub the string content (don't fully redact)
    if (parentKey && isSensitiveKey(parentKey)) {
      return scrubString(obj);
    }
    return scrubString(obj);
  }

  // Handle objects
  if (obj && typeof obj === 'object') {
    const scrubbed: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // If key is sensitive
      if (isSensitiveKey(key)) {
        if (typeof value === 'string') {
          // Scrub string content
          scrubbed[key] = scrubString(value);
        } else if (Array.isArray(value)) {
          // For arrays with sensitive keys, still scrub each element
          scrubbed[key] = value.map((item) => {
            if (typeof item === 'string') {
              return scrubString(item);
            }
            return scrubObject(item);
          });
        } else {
          // For non-string, non-array values with sensitive keys, fully redact
          scrubbed[key] = '[REDACTED]';
        }
        continue;
      }
      scrubbed[key] = scrubObject(value, key);
    }
    return scrubbed;
  }

  return obj;
};

/**
 * Scrub a single string using all PII patterns
 * Order matters: More specific patterns first (phone numbers before license numbers)
 */
const scrubString = (str: string): string => {
  let scrubbed = str;

  // Order: Most specific first
  scrubbed = scrubbed.replace(PII_PATTERNS.mollieToken, '[MOLLIE_TOKEN]');
  scrubbed = scrubbed.replace(PII_PATTERNS.phoneNumber, '[PHONE]'); // Phone before license (more specific)
  scrubbed = scrubbed.replace(PII_PATTERNS.iban, '[IBAN]');
  scrubbed = scrubbed.replace(PII_PATTERNS.licenseNumber, '[LICENSE]'); // License after phone
  scrubbed = scrubbed.replace(PII_PATTERNS.email, '[EMAIL]');

  return scrubbed;
};

/**
 * Check if object key itself is sensitive (e.g., "password", "iban")
 */
const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  const sensitiveKeys = ['password', 'passwd', 'pwd', 'secret', 'token', 'apikey', 'api_key', 'iban', 'bic', 'license', 'licensenumber', 'phone', 'mobile', 'patientcount', 'mollieaccesstoken', 'mollierefreshtoken'];
  return sensitiveKeys.some((k) => lowerKey.includes(k));
};

/**
 * Scrub breadcrumb (user action trail)
 */
const scrubBreadcrumb = (breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb => {
  const scrubbed = { ...breadcrumb };

  if (scrubbed.message) {
    scrubbed.message = scrubString(scrubbed.message);
  }

  if (scrubbed.data) {
    scrubbed.data = scrubObject(scrubbed.data);
  }

  return scrubbed;
};

/**
 * Scrub HTTP request data
 */
const scrubRequest = (request: Sentry.Event['request']): Sentry.Event['request'] | undefined => {
  if (!request) return request;
  const scrubbed = { ...request };

  // Scrub query string (can be string or QueryParams object)
  if (scrubbed.query_string) {
    if (typeof scrubbed.query_string === 'string') {
      scrubbed.query_string = scrubString(scrubbed.query_string);
    } else if (typeof scrubbed.query_string === 'object') {
      scrubbed.query_string = scrubObject(scrubbed.query_string);
    }
  }

  // Scrub POST body
  if (scrubbed.data) {
    scrubbed.data = scrubObject(scrubbed.data);
  }

  // Scrub headers (may contain Authorization tokens)
  if (scrubbed.headers) {
    const scrubbedHeaders: Record<string, string> = {};
    for (const [key, value] of Object.entries(scrubbed.headers)) {
      if (key.toLowerCase() === 'authorization' || key.toLowerCase() === 'cookie') {
        scrubbedHeaders[key] = '[REDACTED]';
      } else {
        scrubbedHeaders[key] = scrubString(String(value));
      }
    }
    scrubbed.headers = scrubbedHeaders;
  }

  return scrubbed;
};

/**
 * Test function to validate scrubbing (used in automated tests)
 */
export const testScrubbing = () => {
  const testCases = [
    {
      input: 'License: 1234567',
      expected: 'License: [LICENSE]',
    },
    {
      input: 'Call +49 30 12345678',
      expected: 'Call [PHONE]',
    },
    {
      input: 'IBAN: DE89370400440532013000',
      expected: 'IBAN: [IBAN]',
    },
    {
      input: 'Email: test@example.com',
      expected: 'Email: [EMAIL]',
    },
    {
      input: 'Token: access_abc123def456ghi789',
      expected: 'Token: [MOLLIE_TOKEN]',
    },
  ];

  testCases.forEach(({ input, expected }, idx) => {
    const result = scrubString(input);
    if (result !== expected) {
      console.error(`❌ Test ${idx + 1} failed:`, { input, expected, result });
    } else {
      console.log(`✅ Test ${idx + 1} passed`);
    }
  });
};
